
















































import { api } from "@/api/api";
import { ApiGetNusCodeDto, ApiUpsertCurriculumDto } from "@/api/generated/Api";
import CourseDescriptionStep from "@/components/curriculum/new-steps/CourseDescriptionStep.vue";
import GeneralInfoStepVue from "@/components/curriculum/new-steps/GeneralInfoStep.vue";
import LearningObjectivesStep from "@/components/curriculum/new-steps/LearningObjectivesStep.vue";
import LearningRequirementsStep from "@/components/curriculum/new-steps/LearningRequirementsStep.vue";
import TargetAudienceStep from "@/components/curriculum/new-steps/TargetAudienceStep.vue";
import TeachingMaterials from "@/components/curriculum/new-steps/TeachingMaterials.vue";
import UploadFile from "@/components/curriculum/UploadFile.vue";
import BaseModal from "@/components/shared/BaseModal.vue";
import BaseStepperDynamic from "@/components/shared/stepper/BaseStepperDynamic.vue";
import { NotificationItemType } from "@/shared/enums/notificationItemEnum";
import { isVocationalSchool } from "@/shared/helpers/curriculumHelpers";
import { globalLoadingWrapper } from "@/shared/helpers/loadingHelpers";
import { openNotification, runStoreAction } from "@/shared/helpers/store.helpers";
import { useRoute, useRouter, useStore } from "@/shared/useHelpers";
import { UpsertCurriculumDto } from "@/store/modules/plans/interfaces/UpsertCurriculumDto.interface";
import { StoreModules } from "@/store/store-modules.enum";
import { StoreActions } from "@/store/store.actions";
import { StoreState } from "@/store/store.state.interface";
import { defineComponent, onMounted, Ref, ref } from "@vue/composition-api";
import { Route } from "vue-router";
import { Store } from "vuex";

interface ComponentProps {
  isDraft: boolean;
  isEditing: boolean;
}

export default defineComponent({
  name: "StudieplanUpsertPage",
  components: {
    UploadFile,
    BaseStepperDynamic,
    BaseModal,
  },
  props: {
    isDraft: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: false },
  },
  setup(props: ComponentProps, context) {
    // TODO Refactor component and split logic to better handle different
    // overlapping states (create, create draft, edit, edit draft)
    const store = useStore<StoreState>();
    const route = useRoute();
    const router = useRouter();
    const displayCreateCurriculumModal = ref(false);
    const savingDraft = ref(false);
    const createdCurriculum = ref();
    const curriculumValues = ref(getInitialCurriculumValues(route));
    const nusCodes = ref<ApiGetNusCodeDto[]>([]);
    const curriculumId = router.currentRoute.params.id;
    const { selectedSteps } = useSelectedSteps(context.refs, +route.query.mainCourseId);

    onMounted(async () => {
      globalLoadingWrapper({ blocking: true }, async () => {
        try {
          await fetchValues(store);

          if (props.isDraft) {
            // Edit draft
            curriculumValues.value = (await api.curriculum.getDraftPerIdAndUserId(router.currentRoute.params.id)).data;
            fetchNusCodes();

            if (isVocationalSchool(+route.query.mainCourseId) && !curriculumValues.value.vocationalSchool) {
              curriculumValues.value.vocationalSchool = {
                practice: false,
                subjectCount: 1,
              };
            }

            return;
          }

          if (curriculumId) {
            // Edit course
            const curriculum = (await api.curriculum.getCurriculumAsync(+curriculumId)).data;
            curriculumValues.value = {
              ...curriculum,
              course: {
                ...curriculum.course,
                teachingMaterials: curriculum.course?.teachingMaterials?.map((teachingMaterial) => teachingMaterial.id),
              },
              partners: curriculum.partners?.map((partner) => partner.customerId),
            };

            fetchNusCodes(curriculum.orgId);

            if (isVocationalSchool(+route.query.mainCourseId) && !curriculum.vocationalSchool) {
              curriculumValues.value.vocationalSchool = {
                practice: false,
                subjectCount: 1,
              };
            }

            return;
          }

          // Create course
          if (!route.query.teachingMethod || !route.query.mainCourseId || !route.query.subCourseId) {
            openNotification(
              store,
              NotificationItemType.Error,
              "Mangler hovedområde, kursområde eller undervisningsform"
            );
            router.push(`/studieplaner/ny`);
          }
          // Fetch NUS codes for for create new curriculum
          fetchNusCodes();
        } catch (e) {
          router.push(`/studieplaner/ny`);
        }
      });
    });

    const fetchNusCodes = async (orgId?: number) => {
      // NUS codes are only relevant for curriculum with main course vocational schools
      if (!isVocationalSchool(+route.query.mainCourseId)) {
        return;
      }

      nusCodes.value = (await api.curriculum.getNusCodes({ orgId })).data.filter((x) => x.isActive);
    };

    const cancel = () => {
      router.push(`/studieplaner`);
      return true;
    };
    const saveDraft = async () => {
      globalLoadingWrapper({ blocking: true }, async () => {
        await saveDraftAsync(store, savingDraft, curriculumValues);
      });
    };
    const approveCurriculum = async () => {
      globalLoadingWrapper({ blocking: true }, async () => {
        await sendCurriculumToApprovalAsync(store, createdCurriculum, curriculumValues, +curriculumId);
      });
    };

    return {
      selectedSteps,
      curriculumValues,
      createdCurriculum,
      displayCreateCurriculumModal,
      savingDraft,
      saveDraft,
      cancel,
      approveCurriculum,
      createCurriculumModalTitle: route.params.id ? "Last opp filer" : "Bekreft studieplanen",
      nusCodes,
    };
  },
});

const getInitialCurriculumValues = (route: Route): UpsertCurriculumDto => {
  const curriculumValues: UpsertCurriculumDto = {
    subCourseId: +route.query.subCourseId,
    teachingMethod: route.query?.teachingMethod?.toString(),
    hoursMin: 0,
    hoursMax: 0,
    levelId: 0,
    evaluationId: 0,
    teacherRequired: false,
    foreknowledgeRequired: false,
    courseCertificateRequired: false,
    teacherRequirements:
      "<p><strong>Teksten bør besvare:</strong></p><ul><li><p>Hvilken utdannelse kreves av underviser eller veileder for dette kurset eller studie?</p></li><li><p>Bør lærer eller veileder ha erfaring med undervisning av voksne?</p></li></ul>",
    targetGroup:
      "<p><strong>Eksempler på formuleringer for målgruppe:</strong></p><ul><li><p>Personer som vil forberede seg teoretisk for å kunne gjennomføre tverrfaglig teoretisk eksamen og senere avlegge praktisk fagprøve for å få fagbrev</p></li><li><p>Personer som ønsker å heve sin norskkompetanse</p></li><li><p>Personer som ønsker å videreutdanne seg</p></li><li><p>Personer som ønsker spisskompetanse innenfor sitt fagfelt</p></li></ul>",
    foreknowledgeRequirements:
      "<p><strong>Teksten bør beskrive krav til forkunnskaper innen:</strong></p><ul><li><p>Erfaring</p></li><li><p>Utdannelse</p></li><li><p>Språknivå</p></li></ul>",
    learningObjectives:
      "<p><strong>Teksten bør besvare:</strong></p><ul><li><p>Hvilke læringsmål eller kompetansemål er det for kurset eller studiet?</p></li><li><p>Er disse læringsmålene gitt fra f.eks Utdanningsdirektoratet? Kompetanse Norge? AOF? Miljødirektoratet?</p></li><li><p>Hva skal kursdeltakeren eller studenten sitte igjen med?</p></li></ul><p><strong>Husk</strong>: Det skal alltid brukes kursdeltaker (ikke person/gruppe/enkeltmenneske) og på fagskolen skal det brukes student</p>",
    course: {
      method:
        "<p><strong>Teksten bør beskrive:</strong></p><ul><li><p>Generelt (om hva AOF ser på som viktig, her kan det ligge inne en standard tekst, se eksisterende forslag)</p></li><li><p>Spesifikt om dette opplæringstilbudet</p></li><li><p>Fysisk klasseromskurs</p></li><li><p>Virtuelt klasserom</p></li><li><p>Nettkurs</p></li><li><p>Kombinert løsning med fysisk/virtuelt og nett</p></li><li><p>Inneholder kurset selvstudier?</p></li><li><p>Andre ressurser (Migranorsk, word, excel, AOF Nettopp Nettopp, Datapower, Vignita)</p></li></ul><p><strong>Generell tekst:</strong></p><p>AOF er spesialister på opplæring spesielt organisert for voksne. Som utdanningsaktør skal AOF være unike når det gjelder å tilby meningsfull og relevant opplæring. Med sine tette relasjoner til samfunn og arbeidsliv skal AOF tilby en opplæring som tilfredsstiller voksnes krav til relevans og anvendbarhet, samtidig som læringen tar utgangspunkt i den enkeltes forutsetninger, ønsker og behov. Det man tilegner seg av læring gjennom AOF den ene dagen skal kunne anvendes på jobb den neste dagen!</p><p>AOFs syn på mennesket, kunnskap og læring ligger til grunn for all pedagogisk aktivitet. Voksne er ikke passive mottakere av læring, men skal være aktive deltagere i læringsprosessen. Det må gis rom for deltakelse og muligheter for deltagernes påvirkning og selvstyring.</p><p>AOFs pedagogiske grunntanke er å legge til rette for at deltakerne inviteres til utprøving, oppdagelse og refleksjon rundt egen og andres hverdag og ståsted. Utvikling av kompetanse hos den enkelte øker når den skjer i et sosialt fellesskap. Klasser/grupper som preges av aktivt sosialt samspill og gjensidig hjelp deltagerne imellom, bidrar til økt læringsutbytte. Læring og kunnskapsutvikling skjer også i samtalen mellom veileder og deltakere.</p><p>Arbeidsmetodene som benyttes skal stimulere deltagerne til bevisstgjøring rundt egen læring, refleksjon og kritisk tenkning. AOF som opplæringsaktør må derfor ha et bevisst forhold til valg av metodiske tilnærminger, og det er stort behov for ulike handlingsstrategier i det pedagogiske arbeidet. Eksempler på forskjellige arbeidsmetoder kan være dialog, pararbeid, individuelle oppgaver, praktiske øvelser, tavleundervisning, gruppearbeid, individuelt arbeid, klassediskusjon, film, ekskursjoner, demonstrasjon i klasserom og ute eller gjesteforelesere på spesielle tema.</p>",
      content:
        "<p><strong>Teksten bør besvare:</strong></p><ul><li><p>Hva er innholdet/hovedområder i kurset, dette henger sammen med læringsmålene.</p></li><li><p>Er det hovedområder i kurset som læringsmålene går inn under?</p><ul><li><p>Eks: Hvis læringsmålet handler om ergonomi så er generelt innhold «HMS-perspektivet»</p></li><li><p>Eks: på teori til fagprøven er det hovedområder gitt i læreplanen fra Utdanningsdirektoratet.</p></li></ul></li></ul>",
      teachingMaterials: [],
    },
  };
  if (isVocationalSchool(+route.query.mainCourseId)) {
    curriculumValues.vocationalSchool = {
      subjectCount: 1,
      practice: true,
    };
  }
  return curriculumValues;
};

const fetchValues = async (store: Store<StoreState>) => {
  await Promise.all([
    runStoreAction(store, StoreModules.Plans, StoreActions.PlansActions.FetchDefaultOptions),
    runStoreAction(store, StoreModules.Contacts, StoreActions.ContactsActions.FetchOrganisations),
    runStoreAction(store, StoreModules.SubjectRegister, StoreActions.SubjectRegisterActions.FetchSubjectRegister),
    runStoreAction(store, StoreModules.TeachingMaterials, StoreActions.TeachingMaterialsActions.FetchTeachingmaterials),
    runStoreAction(
      store,
      StoreModules.MemberOrganizations,
      StoreActions.MemberOrganizationsActions.FetchMemberOrganizations
    ),
  ]);
};

const useSelectedSteps = (refs: any, mainCourseId: number) => {
  const selectedSteps = ref();
  selectedSteps.value = [
    {
      name: "Generell Info",
      isAvailable: true,
      id: 1,
      component: GeneralInfoStepVue,
    },
    {
      name: "Lærerkrav",
      isAvailable: true,
      id: 2,
      component: LearningRequirementsStep,
    },
    {
      name: "Målgruppe",
      isAvailable: true,
      id: 3,
      component: TargetAudienceStep,
    },
    {
      name: isVocationalSchool(mainCourseId) ? "Læringsutbytte" : "Læringsmål",
      isAvailable: true,
      id: 4,
      component: LearningObjectivesStep,
    },
    {
      name: "Beskrivelse",
      isAvailable: true,
      id: 5,
      component: CourseDescriptionStep,
    },
    {
      name: "Læremidler",
      isAvailable: true,
      id: 6,
      component: TeachingMaterials,
    },
  ].map((step) => {
    const ref = () => refs[`step${step.id}`]?.[0];
    return new Proxy(step, {
      get(target: any, name: string) {
        return target[name] ?? ref()?.[name];
      },
    });
  });
  return { selectedSteps };
};

const saveDraftAsync = async (
  store: Store<StoreState>,
  savingDraft: Ref<boolean>,
  curriculumValues: Ref<UpsertCurriculumDto>
) => {
  if (savingDraft.value) {
    return;
  }
  savingDraft.value = true;
  const draftBody = curriculumValues.value;
  try {
    if (draftBody.draftId && typeof draftBody.draftId === "string") {
      await api.curriculum.updateDraftAsync(draftBody.draftId, draftBody as ApiUpsertCurriculumDto);
      openNotification(store, NotificationItemType.Success, "Utkast oppdatert");
    } else {
      delete draftBody.draftId;
      const createDraftRespone = await api.curriculum.createDraftAsync(draftBody as ApiUpsertCurriculumDto);
      const id = createDraftRespone.data.id;
      curriculumValues.value.draftId = id;
      openNotification(store, NotificationItemType.Success, `Utkastet ble lagret med ID: ${id}`);
    }
  } catch (err: any) {
    openNotification(store, NotificationItemType.Error, "Det oppstod en feil under lagring av utkast.");
  } finally {
    savingDraft.value = false;
  }
};

const createCurriculum = async (curriculumValues: ApiUpsertCurriculumDto) => {
  const response = await api.curriculum.createCurriculumAsync(curriculumValues);
  return response.data.id;
};

const updateCurriculum = async (curriculumId: number, curriculumValues: ApiUpsertCurriculumDto, statusId?: number) => {
  await api.curriculum.updateCurriculumAsync(curriculumId, curriculumValues);
  if (statusId) {
    await api.curriculum.approveCurriculumAsync(curriculumId, { statusId });
  }
  return curriculumId;
};

const sendCurriculumToApprovalAsync = async (
  store: Store<StoreState>,
  createdCurriculum: Ref,
  curriculumValues: Ref<UpsertCurriculumDto>,
  updateCurriculumId?: number
) => {
  try {
    // FIXME Not ideal to lookup approval status by description
    const pendingApprovalStatusId = store.state.plans.defaultOptions.courseStatuses?.find(
      (status) => status.description?.toLocaleLowerCase() === "til godkjenning"
    )?.id;

    // TODO Probably safe assertions below, but should be resolved along with other assertions when refactoring this component
    const id = updateCurriculumId
      ? await updateCurriculum(
          updateCurriculumId,
          curriculumValues.value as ApiUpsertCurriculumDto,
          pendingApprovalStatusId
        )
      : await createCurriculum(curriculumValues.value as ApiUpsertCurriculumDto);

    createdCurriculum.value = {
      ...curriculumValues.value,
      id,
    };
    openNotification(store, NotificationItemType.Success, `Studieplanen ble sendt til godkjenning med ID ${id}`);
  } catch (err: any) {
    err.response.data.forEach((currentError: string) => {
      openNotification(store, NotificationItemType.Error, `Valideringsfeil: ${currentError}`);
    });
  } finally {
    //  TODO: setShowLoadingSpinner(store, false);
  }
};
